import AddIcon from "@mui/icons-material/Add";
import AutoGraphIcon from "@mui/icons-material/AutoGraph";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import TuneIcon from "@mui/icons-material/Tune";
import {
  Grid,
  Pagination,
  TableCell,
  TableFooter,
  TableRow,
  Typography,
} from "@mui/material";
import { uniq } from "lodash";
import moment from "moment";
import { useEffect, useState } from "react";
import { flushSync } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import CustomButton from "../../../../components/CustomButton";
import CustomCheckbox from "../../../../components/CustomCheckbox";
import CustomModal from "../../../../components/CustomModal/CustomModal";
import CustomTextField from "../../../../components/CustomTextField";
import CustomSelect from "../../../../components/Select/Select";
import {
  getAllStockBatchReportData,
  getStockBatchReportData,
} from "../../../../redux/reducers/reportSlice";
import { AppColors } from "../../../../util/AppColors";
import { w20h20BlueIcon } from "../../../../util/CommonIconStyle";
import { expiryStatusList } from "../../../../util/dropList";
import ReportTable from "../ReportTable";
import { nameExpan } from "../tableHeaders";

const tableHdrs = [
  "productName",
  "batchNumber",
  "location",
  "quantity",
  "expiryDate",
  "daysUntilExpiry",
  "status",
  "tradeName",
  "category",
  "hsnCode",
  "barcodeNo",
  "subunits",
  "mfgDate",
  "unitSellingPrice",
  "landedCost",
];
const initSearch = {
  productName: "",
  batchNo: "",
  location: [],
  category: [],
};
const initFilter = {
  visible: false,
  added: false,
  selectedList: [],
  values: {},
};
const filterByList = [
  "Quantity",
  "Expiry Date",
  "Days Until Expiry",
  "Status",
  "Trade Name",
  "HSN Code",
  "Barcode Number",
  "Subunit Qty",
  "Mfg. Date",
  "Unit Selling Price",
  "Landed Cost",
];
const dropFields = ["Status"];
const splitRows = [
  "Quantity",
  "Expiry Date",
  "Days Until Expiry",
  "Subunit Qty",
  "Mfg. Date",
  "Unit Selling Price",
  "Landed Cost",
];

const StockBatchReport = () => {
  const dispatch = useDispatch();
  const [crntTblHeders, setCrntTblHeders] = useState(tableHdrs?.slice(0, 5));
  const [activeTableHeaders, setActiveTableHeaders] = useState(
    tableHdrs?.slice(0, 5)?.map((pd, i) => ({ name: pd, isSelected: true, i })),
  );
  const [editColModVsble, setEditColModVsble] = useState(false);
  const [page, setPage] = useState(1);
  const [searchValues, setSearchValues] = useState(initSearch);
  const [filterValues, setFilterValues] = useState(initFilter);
  const { stockBatch, stockBatchAll } = useSelector((state) => state?.report);
  const rowsPerPage = 10;

  useEffect(() => {
    dispatch(getAllStockBatchReportData());
  }, [dispatch]);

  useEffect(() => {
    onApiCall(1);
  }, [
    searchValues?.productName,
    searchValues?.batchNo,
    searchValues?.location,
    searchValues?.category,
  ]);

  const getFilterObj = (currentPage = 1, reset = false) => {
    setPage(currentPage);
    const reqObj = { type: "batch", page: currentPage, itemSize: rowsPerPage };
    if (reset) return reqObj;
    if (searchValues?.productName?.length > 0) {
      reqObj.productName = [searchValues?.productName];
    }
    if (searchValues?.batchNo?.length > 0) {
      reqObj.batchNo = [searchValues?.batchNo];
    }
    if (searchValues?.location?.length > 0) {
      reqObj.location = searchValues?.location?.map((li) => li?.value);
    }
    if (searchValues?.category?.length > 0) {
      reqObj.category = searchValues?.category?.map((li) => li?.value);
    }

    if (filterValues?.selectedList?.includes("Quantity")) {
      reqObj.minQuantity = Number(filterValues?.values?.["Min Quantity"] ?? 0);
      reqObj.maxQuantity = Number(filterValues?.values?.["Max Quantity"] ?? 0);
    }
    if (filterValues?.selectedList?.includes("Expiry Date")) {
      reqObj.startExpiryDate = moment(
        new Date(filterValues?.values?.["Start Expiry Date"] ?? new Date()),
      ).format("YYYY-MM-DD");
      reqObj.endExpiryDate = moment(
        new Date(filterValues?.values?.["End Expiry Date"] ?? new Date()),
      ).format("YYYY-MM-DD");
    }
    if (filterValues?.selectedList?.includes("Days Until Expiry")) {
      reqObj.minDaysUntilExpiry = Number(
        filterValues?.values?.["Min Days Until Expiry"] ?? 0,
      );
      reqObj.maxDaysUntilExpiry = Number(
        filterValues?.values?.["Max Days Until Expiry"] ?? 0,
      );
    }
    if (filterValues?.selectedList?.includes("Status")) {
      reqObj.status = filterValues?.values?.["Status"]?.map((li) => li?.value);
    }
    if (filterValues?.selectedList?.includes("Trade Name")) {
      reqObj.tradeName = [filterValues?.values?.["Trade Name"] ?? ""];
    }
    if (filterValues?.selectedList?.includes("HSN Code")) {
      reqObj.hsnSacCode = [filterValues?.values?.["HSN Code"] ?? ""];
    }
    if (filterValues?.selectedList?.includes("Barcode Number")) {
      reqObj.barcodeNo = [filterValues?.values?.["Barcode Number"] ?? ""];
    }
    if (filterValues?.selectedList?.includes("Subunit Qty")) {
      reqObj.minSubunits = Number(
        filterValues?.values?.["Min Subunit Qty"] ?? 0,
      );
      reqObj.maxSubunits = Number(
        filterValues?.values?.["Max Subunit Qty"] ?? 0,
      );
    }
    if (filterValues?.selectedList?.includes("Mfg. Date")) {
      reqObj.startMfgDate = moment(
        new Date(filterValues?.values?.["Start Mfg. Date"] ?? new Date()),
      ).format("YYYY-MM-DD");
      reqObj.endMfgDate = moment(
        new Date(filterValues?.values?.["End Mfg. Date"] ?? new Date()),
      ).format("YYYY-MM-DD");
    }
    if (filterValues?.selectedList?.includes("Unit Selling Price")) {
      reqObj.minUnitSellingPrice = Number(
        filterValues?.values?.["Min Unit Selling Price"] ?? 0,
      );
      reqObj.maxUnitSellingPrice = Number(
        filterValues?.values?.["Max Unit Selling Price"] ?? 0,
      );
    }
    if (filterValues?.selectedList?.includes("Landed Cost")) {
      reqObj.minLandedCost = Number(
        filterValues?.values?.["Min Landed Cost"] ?? 0,
      );
      reqObj.maxLandedCost = Number(
        filterValues?.values?.["Max Landed Cost"] ?? 0,
      );
    }
    return reqObj;
  };

  const onApiCall = (pge = 1, reset = false) => {
    setPage(pge);
    dispatch(getStockBatchReportData(getFilterObj(pge, reset)));
  };

  const handleEditColumn = () => {
    const reqList = tableHdrs?.map((pd, i) => {
      const isObjMat = activeTableHeaders?.find(
        (tblHdr) => tblHdr?.name === pd,
      );
      return { name: pd, isSelected: isObjMat?.isSelected ?? false, i };
    });
    setActiveTableHeaders(reqList);
    setEditColModVsble(true);
  };

  const onEditModClose = () => {
    setEditColModVsble(false);
  };

  const handleSelcetdColumn = (selected, ind) => {
    const reqList = activeTableHeaders?.map((act, i) => {
      if (i === ind) return { ...act, isSelected: selected };
      return act;
    });
    setActiveTableHeaders(reqList);
  };

  const handleSaveTableColumn = () => {
    const reqList = activeTableHeaders
      ?.filter((act) => act?.isSelected)
      ?.map((act) => {
        return act?.name;
      });
    setCrntTblHeders(reqList);
    setEditColModVsble(false);
  };

  const totalPage = Math.ceil((stockBatch?.totalRecords ?? 0) / rowsPerPage);

  const handleChangePage = (e, selectedPage) => {
    onApiCall(selectedPage);
  };

  const getTableData = () => {
    return (stockBatch?.data ?? [])?.map((li) => {
      const expiryStatus =
        li?.daysUntilExpiry < 30
          ? "Critical"
          : li?.daysUntilExpiry > 30 && li?.daysUntilExpiry < 60
            ? "Near Expiry"
            : li?.daysUntilExpiry > 60 && li?.daysUntilExpiry < 90
              ? "Attention Required"
              : null;
      return {
        ...li,
        batchNumber: li?.batchNo,
        mfgDate:
          li?.mfgDate?.length > 0
            ? moment(new Date(li?.mfgDate)).format("DD MMM YYYY")
            : null,
        expiryDate:
          li?.expiryDate?.length > 0
            ? moment(new Date(li?.expiryDate)).format("DD MMM YYYY")
            : null,
        landedCost: Number(li?.landedCost ?? 0)?.toFixed(2),
        unitSellingPrice: Number(li?.unitSellingPrice ?? 0)?.toFixed(2),
        hsnCode: li?.hsnSacCode,
        status: !expiryStatus ? null : (
          <div
            className={`con-fit fs12 white-color ph10 pv2 br15
                ${
                  expiryStatus?.toLowerCase() === "critical"
                    ? "card-bot-red-back"
                    : expiryStatus?.toLowerCase() === "near expiry"
                      ? "card-top-orange-color"
                      : "card-top-yellow-color"
                }`}
          >
            {expiryStatus}
          </div>
        ),
      };
    });
  };

  const handleOpenChart = () => {
    console.log("Log triggered");
  };

  const onSearchChange = (e) => {
    const { name, value } = e?.target;
    setSearchValues({
      ...searchValues,
      [name?.trim()?.replace(/,$/, "")]: value, // remove spaces & trailing commas
    });
  };

  const onReset = () => {
    flushSync(() => {
      setSearchValues(initSearch);
    });
    flushSync(() => {
      setFilterValues({ ...initFilter, selectedList: [], values: {} });
    });
    onApiCall(1, true);
  };

  const onFilterChange = (name, value) => {
    const reqObj = { ...filterValues, [name]: value };
    setFilterValues(reqObj);
  };

  const getProductNames = () => {
    const reqList = (stockBatchAll?.data ?? [])
      ?.map((li) => li?.productName)
      ?.filter(Boolean);
    const uniqList = uniq(reqList);
    return uniqList;
  };

  const getBatchNos = () => {
    const reqList = (stockBatchAll?.data ?? [])
      ?.map((li) => li?.batchNo)
      ?.filter(Boolean);
    const uniqList = uniq(reqList);
    return uniqList;
  };

  const getLocationList = () => {
    const reqList = (stockBatchAll?.data ?? [])
      ?.map((li) => li?.locationId)
      ?.filter(Boolean);
    const uniqList = uniq(reqList);
    const reqDropList = uniqList?.map((li) => {
      const reqObj = (stockBatchAll?.data ?? [])?.find(
        (stc) => stc?.locationId?.toString() === li?.toString(),
      );
      return { name: reqObj?.location, value: li?.toString() };
    });
    return reqDropList;
  };

  const getCategoryList = () => {
    const reqList = (stockBatchAll?.data ?? [])
      ?.map((li) => li?.category)
      ?.filter(Boolean);
    const uniqList = uniq(reqList);
    const reqDropList = uniqList?.map((li) => ({ name: li, value: li }));
    return reqDropList;
  };

  const onApplyFilter = () => {
    onApiCall();
    const reqObj = { ...filterValues, visible: false };
    setFilterValues(reqObj);
  };

  const onSelectFilter = (name) => {
    const reqObj = { ...filterValues };
    let reqList = filterValues?.selectedList;
    if (reqList?.includes(name)) {
      reqList = reqList?.filter((item) => item !== name);
    } else {
      reqList.push(name);
    }
    reqObj.selectedList = reqList;
    setFilterValues(reqObj);
  };

  const onCloseFilter = () => {
    const reqObj = { ...filterValues, visible: false };
    setFilterValues(reqObj);
  };

  const onFilterValuesChange = (e) => {
    const { name, value } = e?.target;
    const reqObj = {
      ...filterValues,
      values: {
        ...filterValues?.values,
        [name?.trim()?.replace(/,$/, "")]: value,
      },
    };
    setFilterValues(reqObj);
  };

  const onFilterAdd = (value) => {
    const reqObj = { ...filterValues, added: value };
    setFilterValues(reqObj);
  };

  const onFilterReset = () => {
    const reqObj = {
      ...initFilter,
      visible: true,
      selectedList: [],
      values: {},
    };
    setFilterValues(reqObj);
  };

  const getFieldName = (name, type) => {
    if (name === "Quantity") {
      if (type === "start") return "Min Quantity";
      return "Max Quantity";
    }
    if (name === "Expiry Date") {
      if (type === "start") return "Start Expiry Date";
      return "End Expiry Date";
    }
    if (name === "Days Until Expiry") {
      if (type === "start") return "Min Days Until Expiry";
      return "Max Days Until Expiry";
    }
    if (name === "Subunit Qty") {
      if (type === "start") return "Min Subunit Qty";
      return "Max Subunit Qty";
    }
    if (name === "Mfg. Date") {
      if (type === "start") return "Start Mfg. Date";
      return "End Mfg. Date";
    }
    if (name === "Unit Selling Price") {
      if (type === "start") return "Min Unit Selling Price";
      return "Max Unit Selling Price";
    }
    if (name === "Landed Cost") {
      if (type === "start") return "Min Landed Cost";
      return "Max Landed Cost";
    }
  };

  const getTradeNameList = () => {
    const reqList = (stockBatchAll?.data ?? [])
      ?.map((li) => li?.tradeName)
      ?.filter(Boolean);
    const uniqList = uniq(reqList);
    return uniqList;
  };

  const getHSNCodeList = () => {
    const reqList = (stockBatchAll?.data ?? [])
      ?.map((li) => li?.hsnSacCode)
      ?.filter(Boolean);
    const uniqList = uniq(reqList);
    return uniqList;
  };

  const getBarcodeNoList = () => {
    const reqList = (stockBatchAll?.data ?? [])
      ?.map((li) => li?.barcodeNo)
      ?.filter(Boolean);
    const uniqList = uniq(reqList);
    return uniqList;
  };

  const getSuggestionList = (typ) => {
    return typ === "Trade Name"
      ? getTradeNameList()
      : typ === "HSN Code"
        ? getHSNCodeList()
        : typ === "Barcode Number"
          ? getBarcodeNoList()
          : [];
  };

  return (
    <>
      <div className="flex-row mv10">
        <div className="top-row-cen-con w100Per">
          <div className="w15Per">
            <CustomTextField
              options={getProductNames()}
              value={searchValues?.productName}
              name="productName"
              handleChange={onSearchChange}
              label="Product Name"
              backgroundColor={AppColors.lightBlue2}
              fullWidth
              suggetions
            />
          </div>
          <div className="w15Per ml20">
            <CustomTextField
              options={getBatchNos()}
              value={searchValues?.batchNo}
              name="batchNo"
              handleChange={onSearchChange}
              label="Batch Number"
              backgroundColor={AppColors.lightBlue2}
              fullWidth
              suggetions
            />
          </div>
          <div className="w15Per ml20">
            <CustomSelect
              list={getLocationList()}
              value={searchValues?.location ?? []}
              name="location"
              handleChange={onSearchChange}
              label="Location"
              backgroundColor={AppColors.lightBlue2}
              multiSelectSearchCheck
            />
          </div>
          <div className="w15Per ml20">
            <CustomSelect
              list={getCategoryList()}
              value={searchValues?.category ?? []}
              name="category"
              handleChange={onSearchChange}
              label="Category"
              backgroundColor={AppColors.lightBlue2}
              multiSelectSearchCheck
            />
          </div>
          <div
            className="flex-row-ali-cen ml20 filter cursor"
            onClick={() => onFilterChange("visible", true)}
          >
            <TuneIcon sx={{ color: AppColors.appPrimary }} />
            <Typography className="font-medium fs12 blue-color ml10">
              {filterValues?.selectedList?.length > 0
                ? `${filterValues?.selectedList?.length} `
                : ""}
              Filter
            </Typography>
          </div>
          <div className="ml20">
            <CustomButton text="Reset" onClick={onReset} smallBtn redBtn />
          </div>
          <div
            className="flex-row-ali-cen ml20 cursor"
            onClick={handleEditColumn}
          >
            <EditOutlinedIcon sx={w20h20BlueIcon} />
            <Typography className="blue-color fs16 font-medium ml5">
              Edit columns ({crntTblHeders?.length})
            </Typography>
          </div>
          <div
            className="flex-row-ali-cen ml20 cursor"
            onClick={handleOpenChart}
          >
            <AutoGraphIcon sx={w20h20BlueIcon} />
          </div>
        </div>
      </div>
      <Typography className="font-medium fs12 gray28 ml30">
        Number of Records: {stockBatch?.totalRecords ?? 0}
      </Typography>
      <div className="mtMin10">
        <ReportTable datas={getTableData()} columns={crntTblHeders} />
        <div className="flex1-end mtMin30">
          {Math.ceil((stockBatch?.totalRecords ?? 0) / rowsPerPage) > 1 ? (
            <TableFooter>
              <TableRow>
                <TableCell colSpan={12}>
                  <div className="flex1-end">
                    <Pagination
                      count={totalPage}
                      variant="outlined"
                      color="primary"
                      page={page}
                      onChange={handleChangePage}
                    />
                  </div>
                </TableCell>
              </TableRow>
            </TableFooter>
          ) : null}
        </div>
      </div>

      <CustomModal
        open={editColModVsble}
        onClose={onEditModClose}
        header="Columns"
        modal
        modalWidth={25}
      >
        <div className="ph20 scroll-70vh">
          {activeTableHeaders?.map((hr) => (
            <div className="flex-row" key={hr?.i}>
              <CustomCheckbox
                label={nameExpan?.[hr?.name]}
                checked={hr?.isSelected}
                onChange={() => handleSelcetdColumn(!hr?.isSelected, hr?.i)}
              />
            </div>
          ))}
          <div className="flex-end mb10">
            <div>
              <CustomButton
                text="Save"
                smallBtn
                onClick={handleSaveTableColumn}
              />
            </div>
          </div>
        </div>
      </CustomModal>
      <CustomModal
        open={filterValues?.visible}
        onClose={onCloseFilter}
        header="Filter"
        rightModal
        modalWidth={30}
      >
        <div className="scroll-80vh ph20">
          <Grid container spacing={2}>
            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              <Typography className="gray38 fs12 txt-regular">
                Filter by:
              </Typography>
              {!filterValues?.added ? (
                filterByList?.map((li, i) => (
                  <div className="flex-row-ali-cen" key={`${i}_li`}>
                    <div>
                      <CustomCheckbox
                        checked={filterValues?.selectedList?.includes(li)}
                        onChange={() => onSelectFilter(li)}
                      />
                    </div>
                    <Typography className="txt-regular fs14 black mb5 mlMin15">
                      {li}
                    </Typography>
                  </div>
                ))
              ) : (
                <div>
                  <div
                    className="flex-row-ali-cen gray-back4 ph10 pv10 br10 width-fit-con mt10 cursor"
                    onClick={() => onFilterAdd(false)}
                  >
                    <AddIcon
                      sx={{ color: AppColors.appPrimary, fontSize: 15 }}
                    />
                    <Typography className="font-medium fs12 blue-color ml5">
                      Add Filter
                    </Typography>
                  </div>
                  <div>
                    {filterValues?.selectedList?.map((li) => {
                      return (
                        <div className="mt20">
                          {splitRows?.includes(li) ? (
                            <div className="flex-row-between-align-center">
                              <div className="w45Per">
                                <CustomTextField
                                  placeholder={getFieldName(li, "start")}
                                  fullWidth
                                  name={getFieldName(li, "start")}
                                  handleChange={onFilterValuesChange}
                                  value={
                                    filterValues?.values?.[
                                      getFieldName(li, "start")
                                    ] ?? ""
                                  }
                                  label={getFieldName(li, "start")}
                                  mobileDate={
                                    li === "Expiry Date" || li === "Mfg. Date"
                                  }
                                  type="number"
                                />
                              </div>
                              <div className="w45Per">
                                <CustomTextField
                                  placeholder={getFieldName(li, "end")}
                                  fullWidth
                                  name={getFieldName(li, "end")}
                                  handleChange={onFilterValuesChange}
                                  value={
                                    filterValues?.values?.[
                                      getFieldName(li, "end")
                                    ] ?? ""
                                  }
                                  label={getFieldName(li, "end")}
                                  mobileDate={
                                    li === "Expiry Date" || li === "Mfg. Date"
                                  }
                                  type="number"
                                />
                              </div>
                            </div>
                          ) : dropFields?.includes(li) ? (
                            <CustomSelect
                              list={expiryStatusList}
                              value={filterValues?.values?.[li] ?? []}
                              name={li}
                              handleChange={onFilterValuesChange}
                              label={li}
                              multiSelectSearchCheck
                            />
                          ) : (
                            <CustomTextField
                              placeholder={li}
                              fullWidth
                              name={li}
                              handleChange={onFilterValuesChange}
                              value={filterValues?.values?.[li] ?? ""}
                              label={li}
                              suggetions
                              options={getSuggestionList(li)}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {!filterValues?.added ? (
                <div className="flex1-end mt15">
                  <div>
                    <CustomButton
                      text="Add"
                      smallBtn
                      onClick={() => onFilterAdd(true)}
                    />
                  </div>
                </div>
              ) : (
                <div className="flex-row-center mt15">
                  <div>
                    <CustomButton
                      text="Reset All"
                      smallBtn
                      onClick={onFilterReset}
                      tabSelectdBtn
                    />
                  </div>
                  <div className="ml20">
                    <CustomButton
                      text="Apply Filters"
                      smallBtn
                      onClick={onApplyFilter}
                    />
                  </div>
                </div>
              )}
            </Grid>
          </Grid>
        </div>
      </CustomModal>
    </>
  );
};

export default StockBatchReport;
